import { useTableActionContext } from '@aibees/elephant/context';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useGetCreditsAmountTypeOnActionPopup } from '@aibees/elephant/hooks/use-get-credits-amount-type-on-action-popup';
import { ROUTES } from '@aibees/elephant/router';
import { LabelType } from '@aibees/elephant/types/label';
import { useMatchRoute } from '@aibees/shared/hooks';
import {
  ACCORDION_IDS,
  DIALOG_IDS,
  getAccordionIdWithType,
  useTableAccordionStore,
} from '@aibees/shared/store/table-accordion-store';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useMemo } from 'react';

export function useSaveDialog() {
  // Context hooks
  const {
    handleSave,
    handleClose,
    saveDialogState,
    ids,
    previousState,
    setPreviousState,
  } = useTableActionContext();
  const grouping = useLabelGroupingStatus();

  // Other hooks
  const isCompanyPage = useMatchRoute(ROUTES.searchCompany);
  const workspace = useWorkspace();

  // Credit types
  const credits = useGetCreditsAmountTypeOnActionPopup();
  const {
    aiCompany,
    basicPeople,
    aiPeople,
    aiUnsavedPeople,
    basicUnsavedPeople,
  } = credits;

  // Memoized values
  const showAiLookalike = useMemo(
    () =>
      aiCompany ||
      basicPeople ||
      aiPeople ||
      aiUnsavedPeople ||
      basicUnsavedPeople,
    [aiCompany, basicPeople, aiPeople, aiUnsavedPeople, basicUnsavedPeople]
  );

  // Use table accordion store directly
  const {
    getDialogExpanded,
    setDialogExpanded,
    setInitialExpanded,
    clearDialog,
  } = useTableAccordionStore();

  const dialogId = DIALOG_IDS.SAVE_DIALOG;
  const expandedAccordion = getDialogExpanded(dialogId);

  const getInitialAccordionId = useCallback(() => {
    if (previousState === LabelType.LABEL) {
      return getAccordionIdWithType('LABEL');
    }
    if (previousState === LabelType.LIST) {
      return getAccordionIdWithType('LIST');
    }
    if (!isCompanyPage) {
      return ACCORDION_IDS.AI_LOOKALIKE;
    }
    if (workspace) {
      return ACCORDION_IDS.ASSIGN_OWNER;
    }
    return ACCORDION_IDS.ASSIGN_OWNER;
  }, [previousState, isCompanyPage, workspace]);

  // Initialize accordion state when dialog opens
  useEffect(() => {
    if (saveDialogState.value) {
      setInitialExpanded(dialogId, getInitialAccordionId());
    }
  }, [
    saveDialogState.value,
    dialogId,
    setInitialExpanded,
    getInitialAccordionId,
  ]);

  // Clear dialog state when dialog closes
  useEffect(() => {
    if (!saveDialogState.value) {
      clearDialog(dialogId);
    }
  }, [saveDialogState.value, clearDialog, dialogId]);

  // Handlers
  const setExpandedAccordion = useCallback(
    (accordionId: string | null) => {
      setDialogExpanded(dialogId, accordionId);
    },
    [dialogId, setDialogExpanded]
  );

  return {
    // State
    saveDialogState,
    ids,
    expandedAccordion,
    showAiLookalike,
    workspace,
    isCompanyPage,
    grouping,
    // Handlers
    handleSave,
    handleClose,
    setExpandedAccordion,
    setPreviousState,

    // Computed values
    dialogId,
  };
}
